import {
  motion,
  useMotionValue,
  useTransform,
  useSpring,
  useReducedMotion,
} from "framer-motion";

const NODE_HALF = 28; // half of w-14
const COLUMN_GAP = 24; // md:gap-x-6

// grid template per column count, written out in full so Tailwind picks them up
const COLUMN_CLASS = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};

const StepCard = ({ step, index, showConnector }) => {
  const prefersReducedMotion = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [6, -6]), {
    stiffness: 250,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-6, 6]), {
    stiffness: 250,
    damping: 20,
  });

  const handleMouseMove = (e) => {
    if (prefersReducedMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <div className="relative flex h-full flex-row items-start gap-4 text-left md:flex-col md:items-start md:gap-0">
      {/* connector to the next node in the same row - desktop only.
          Runs from this node's centre to the next one: one column + one gap. */}
      {showConnector && (
        <div
          className="pointer-events-none absolute hidden h-0.5 -translate-y-1/2 md:block"
          style={{
            left: NODE_HALF,
            top: NODE_HALF,
            width: `calc(100% + ${COLUMN_GAP}px)`,
          }}
        >
          <div className="h-full w-full bg-[var(--website-secondary-color)] opacity-30" />
          <div
            className="absolute inset-0 h-full w-full opacity-80"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, var(--website-secondary-color) 0 10px, transparent 10px 20px)",
              animation: "processFlow 1.6s linear infinite",
            }}
          />
        </div>
      )}

      {/* numbered node */}
      <div
        className="relative z-10 flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-[var(--website-primary-color)] shadow-[0_8px_20px_-6px_rgba(var(--website-secondary-rgb),0.6)]"
        style={{
          background:
            "linear-gradient(145deg, var(--website-secondary-color) 0%, #00d99a 55%, #00b385 100%)",
        }}
      >
        <span className="drop-shadow-sm">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="pointer-events-none absolute inset-0 rounded-2xl border border-white/40" />
      </div>

      {/* tilt card */}
      <div
        style={{ perspective: 800 }}
        className="min-w-0 flex-1 md:mt-4 md:w-full"
      >
        <motion.div
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          whileHover={{ y: -4 }}
          style={{ rotateX, rotateY, transformPerspective: 800 }}
          className="h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors duration-300 hover:border-[rgba(var(--website-secondary-rgb),0.6)] hover:shadow-lg"
        >
          <h3 className="text-lg font-semibold text-[var(--website-primary-color)]">{step.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            {step.description}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

const ProcessTimeline = ({ steps }) => {
  // 6 steps read better as two rows of 3 than a row of 4 plus a stray row of 2
  const columns =
    steps.length > 4 ? (steps.length % 3 === 0 ? 3 : 4) : steps.length;

  return (
    <div className="relative mt-14">
      {/* vertical flowing line - mobile */}
      <div
        className="pointer-events-none absolute left-7 top-7 block w-0.5 -translate-x-1/2 bg-[var(--website-secondary-color)] opacity-40 md:hidden"
        style={{ bottom: 28 }}
      />

      <style>{`
        @keyframes processFlow {
          from { background-position: 0 0; }
          to { background-position: 20px 0; }
        }
      `}</style>

      <div
        className={`grid grid-cols-1 items-stretch gap-10 md:gap-x-6 md:gap-y-12 ${
          COLUMN_CLASS[columns] ?? "md:grid-cols-4"
        }`}
      >
        {steps.map((step, index) => (
          <StepCard
            key={index}
            step={step}
            index={index}
            // no trailing connector on the last card of a row, or the last overall
            showConnector={
              (index + 1) % columns !== 0 && index !== steps.length - 1
            }
          />
        ))}
      </div>
    </div>
  );
};

export default ProcessTimeline;
